import { useMemo, useCallback } from 'react';
import type { CreateFilamentInput, FilamentWithRelations } from '@maker-toolchest/shared';
import { createFilamentSchema } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { ComboBox } from '@/components/ComboBox';
import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { MultiColorPicker } from '@/components/MultiColorPicker';
import { Button } from '@/components/Button';
import {
  useFilamentLookups,
  useCreateFilamentType,
  useCreateFilamentManufacturer,
  useCreateFilamentFinish,
  useCreateEmbeddedMaterial,
} from '../hooks';
import { useFormState } from '@/hooks/useFormState';
import { zodErrorsToFieldErrors } from '@/utils/formErrors';

interface FilamentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFilamentInput) => Promise<void>;
  filament?: FilamentWithRelations | null;
  loading?: boolean;
}

const defaultFormData: CreateFilamentInput = {
  name: '',
  manufacturerId: '',
  typeId: '',
  colorName: '',
  colorHexes: ['#808080'],
  diameter: 1.75,
  standardSpoolWeight: 1000,
  dryingRequired: false,
};

export function FilamentForm({
  open,
  onClose,
  onSubmit,
  filament,
  loading = false,
}: FilamentFormProps) {
  const { types, manufacturers, finishes, embeddedMaterials, isLoading: lookupsLoading } = useFilamentLookups();

  const createType = useCreateFilamentType();
  const createManufacturer = useCreateFilamentManufacturer();
  const createFinish = useCreateFilamentFinish();
  const createEmbeddedMaterial = useCreateEmbeddedMaterial();

  // Find default values for common fields - memoized to prevent infinite loops
  const defaultTypeId = useMemo(() => {
    const plaType = types.find(t => t.name.toUpperCase() === 'PLA');
    return plaType?.id || '';
  }, [types]);

  const defaultFinishId = useMemo(() => {
    const standardFinish = finishes.find(f => f.name.toLowerCase().includes('standard'));
    return standardFinish?.id || '';
  }, [finishes]);

  // Memoize default values to prevent infinite loops
  const defaultValuesWithDefaults = useMemo(() => ({
    ...defaultFormData,
    typeId: defaultTypeId,
    finishId: defaultFinishId || undefined,
  }), [defaultTypeId, defaultFinishId]);

  // Memoize itemToFormData function to prevent infinite loops
  const itemToFormData = useCallback((f: any): CreateFilamentInput => ({
    name: f.name,
    manufacturerId: f.manufacturerId,
    typeId: f.typeId,
    finishId: f.finishId || undefined,
    embeddedMaterialId: f.embeddedMaterialId || undefined,
    colorName: f.colorName,
    colorHexes: f.colorHexes,
    diameter: f.diameter,
    standardSpoolWeight: f.standardSpoolWeight,
    density: f.density || undefined,
    hotendTempMin: f.hotendTempMin || undefined,
    hotendTempMax: f.hotendTempMax || undefined,
    bedTempMin: f.bedTempMin || undefined,
    bedTempMax: f.bedTempMax || undefined,
    printSpeedMin: f.printSpeedMin || undefined,
    printSpeedMax: f.printSpeedMax || undefined,
    fanSpeedMin: f.fanSpeedMin || undefined,
    fanSpeedMax: f.fanSpeedMax || undefined,
    dryingRequired: f.dryingRequired,
    dryingTemp: f.dryingTemp || undefined,
    dryingTime: f.dryingTime || undefined,
    notes: f.notes || undefined,
  }), []);

  // Use form state hook
  const {
    formData,
    errors,
    handleChange: baseHandleChange,
    setErrors,
    isEditing,
  } = useFormState<CreateFilamentInput>({
    defaultValues: defaultValuesWithDefaults,
    currentItem: filament as CreateFilamentInput | null,
    isOpen: open,
    itemToFormData,
  });

  // Wrap base handleChange with custom logic for required ComboBox fields
  const handleChange = (field: keyof CreateFilamentInput, value: unknown) => {
    // For required ComboBox fields, prevent setting empty strings
    if (field === 'manufacturerId' || field === 'typeId') {
      if (typeof value === 'string' && value.trim() === '') {
        // Don't update the field - keep the previous value for required fields
        return;
      }
    }

    baseHandleChange(field, value);
  };

  const validate = (): boolean => {
    // Validate with Zod schema
    const result = createFilamentSchema.safeParse(formData);

    if (!result.success) {
      const validationErrors = zodErrorsToFieldErrors(result.error);
      setErrors(validationErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(formData);
    } catch {
      // Error handled by caller
    }
  };

  // Build options for selects
  const manufacturerOptions = manufacturers.map((mfr) => ({ value: mfr.id, label: mfr.name }));
  const typeOptions = types.map((type) => ({ value: type.id, label: type.name }));
  const finishOptions = finishes.map((finish) => ({ value: finish.id, label: finish.name }));
  const embeddedMaterialOptions = embeddedMaterials.map((em) => ({ value: em.id, label: em.name }));

  const diameterOptions = [
    { value: '1.75', label: '1.75mm' },
    { value: '2.85', label: '2.85mm' },
  ];

  // Handlers for creating new lookup entries
  const handleCreateManufacturer = async (name: string) => {
    const newManufacturer = await createManufacturer.mutateAsync(name);
    handleChange('manufacturerId', newManufacturer.id);
  };

  const handleCreateType = async (name: string) => {
    const newType = await createType.mutateAsync(name);
    handleChange('typeId', newType.id);
  };

  const handleCreateFinish = async (name: string) => {
    const newFinish = await createFinish.mutateAsync(name);
    handleChange('finishId', newFinish.id);
  };

  const handleCreateEmbeddedMaterial = async (name: string) => {
    const newMaterial = await createEmbeddedMaterial.mutateAsync(name);
    handleChange('embeddedMaterialId', newMaterial.id);
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Filament' : 'Create Custom Filament'}
    >
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="flex-1 space-y-6 overflow-y-auto p-4">
          {/* Base Details - Not Collapsible */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Base Details</h3>
            <Input
              label="Name"
              placeholder="e.g., eSUN PLA+ Black"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
              required
            />
            <ComboBox
              label="Manufacturer"
              value={formData.manufacturerId}
              onChange={(value) => handleChange('manufacturerId', value)}
              onCreateNew={handleCreateManufacturer}
              error={errors.manufacturerId}
              disabled={lookupsLoading}
              options={manufacturerOptions}
              placeholder="Select or create manufacturer..."
              createLabel="Create manufacturer"
              required
            />
            <Input
              label="Color Name"
              placeholder="e.g., Midnight Blue, Galaxy Purple"
              value={formData.colorName}
              onChange={(e) => handleChange('colorName', e.target.value)}
              error={errors.colorName}
              required
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">
                Color Swatches
              </label>
              <MultiColorPicker
                colors={formData.colorHexes}
                onChange={(colors) => handleChange('colorHexes', colors)}
              />
              {errors.colorHexes && (
                <p className="mt-1 text-sm text-error">{errors.colorHexes}</p>
              )}
              <p className="mt-1 text-xs text-foreground-muted">
                Add multiple colors for gradient or multi-color filaments
              </p>
            </div>
          </div>

          {/* Material Properties */}
          <FormSection
            title="Material"
            collapsible
            defaultOpen={false}
            collapsedDescription="Default: PLA, 1.75mm, Standard finish, No drying required"
          >
            <div className="space-y-4">
              <ComboBox
                label="Type"
                value={formData.typeId}
                onChange={(value) => handleChange('typeId', value)}
                onCreateNew={handleCreateType}
                error={errors.typeId}
                disabled={lookupsLoading}
                options={typeOptions}
                placeholder="Select or create type..."
                createLabel="Create type"
                required
              />
              <ComboBox
                label="Finish"
                value={formData.finishId || ''}
                onChange={(value) => handleChange('finishId', value || undefined)}
                onCreateNew={handleCreateFinish}
                disabled={lookupsLoading}
                options={finishOptions}
                placeholder="Select or create finish..."
                createLabel="Create finish"
              />
              <ComboBox
                label="Embedded Material"
                value={formData.embeddedMaterialId || ''}
                onChange={(value) => handleChange('embeddedMaterialId', value || undefined)}
                onCreateNew={handleCreateEmbeddedMaterial}
                disabled={lookupsLoading}
                options={embeddedMaterialOptions}
                placeholder="Select or create embedded material..."
                createLabel="Create embedded material"
              />
              <Select
                label="Diameter"
                value={String(formData.diameter)}
                onChange={(e) => handleChange('diameter', parseFloat(e.target.value))}
                options={diameterOptions}
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.dryingRequired || false}
                  onChange={(e) => handleChange('dryingRequired', e.target.checked)}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">Requires drying before use</span>
              </label>
              {formData.dryingRequired && (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Drying Temp (°C)"
                    type="number"
                    placeholder="e.g., 50"
                    value={formData.dryingTemp || ''}
                    onChange={(e) => handleChange('dryingTemp', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                  <Input
                    label="Drying Time (hours)"
                    type="number"
                    placeholder="e.g., 4"
                    value={formData.dryingTime || ''}
                    onChange={(e) => handleChange('dryingTime', e.target.value ? parseInt(e.target.value) : undefined)}
                  />
                </div>
              )}
            </div>
          </FormSection>

          {/* Print Settings */}
          <FormSection
            title="Print Settings"
            collapsible
            defaultOpen={false}
            collapsedDescription="Temperature and speed settings (optional)"
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Hotend Temp Min (°C)"
                  type="number"
                  placeholder="e.g., 190"
                  value={formData.hotendTempMin || ''}
                  onChange={(e) => handleChange('hotendTempMin', e.target.value ? parseInt(e.target.value) : undefined)}
                />
                <Input
                  label="Hotend Temp Max (°C)"
                  type="number"
                  placeholder="e.g., 220"
                  value={formData.hotendTempMax || ''}
                  onChange={(e) => handleChange('hotendTempMax', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Bed Temp Min (°C)"
                  type="number"
                  placeholder="e.g., 50"
                  value={formData.bedTempMin || ''}
                  onChange={(e) => handleChange('bedTempMin', e.target.value ? parseInt(e.target.value) : undefined)}
                />
                <Input
                  label="Bed Temp Max (°C)"
                  type="number"
                  placeholder="e.g., 70"
                  value={formData.bedTempMax || ''}
                  onChange={(e) => handleChange('bedTempMax', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Print Speed Min (mm/s)"
                  type="number"
                  placeholder="e.g., 40"
                  value={formData.printSpeedMin || ''}
                  onChange={(e) => handleChange('printSpeedMin', e.target.value ? parseInt(e.target.value) : undefined)}
                />
                <Input
                  label="Print Speed Max (mm/s)"
                  type="number"
                  placeholder="e.g., 80"
                  value={formData.printSpeedMax || ''}
                  onChange={(e) => handleChange('printSpeedMax', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Fan Speed Min (%)"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="e.g., 50"
                  value={formData.fanSpeedMin || ''}
                  onChange={(e) => handleChange('fanSpeedMin', e.target.value ? parseInt(e.target.value) : undefined)}
                />
                <Input
                  label="Fan Speed Max (%)"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="e.g., 100"
                  value={formData.fanSpeedMax || ''}
                  onChange={(e) => handleChange('fanSpeedMax', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </div>
            </div>
          </FormSection>

          {/* Spool Settings */}
          <FormSection
            title="Spool Settings"
            collapsible
            defaultOpen={false}
            collapsedDescription="Default: 1kg (1000g) spool"
          >
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Spool Weight (g)"
                type="number"
                value={formData.standardSpoolWeight}
                onChange={(e) => handleChange('standardSpoolWeight', parseFloat(e.target.value))}
                min={0}
              />
              <Input
                label="Density (g/cm³)"
                type="number"
                step="0.01"
                placeholder="Optional"
                value={formData.density || ''}
                onChange={(e) => handleChange('density', e.target.value ? parseFloat(e.target.value) : undefined)}
              />
            </div>
          </FormSection>

          {/* Notes */}
          <FormSection title="Notes" collapsible defaultOpen={false}>
            <Textarea
              placeholder="Add any notes about this filament..."
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value || undefined)}
              rows={4}
            />
          </FormSection>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-background-subtle px-4 py-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            {isEditing ? 'Save Changes' : 'Create Filament'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
